import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import Style from '../assets/css/myDrawings.module.css';
import { getDatabase, ref, get, update, remove } from 'firebase/database';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { getAuth } from 'firebase/auth';

const MyDrawings = ({ userId }) => {
    const [images, setImages] = useState([]);
    const [editingImage, setEditingImage] = useState(null);
    const [newName, setNewName] = useState('');
    const [newStoryText, setNewStoryText] = useState('');
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchImages = async () => {
            if (!userId) {
                setImages([]);
                return;
            }

            const db = getDatabase(); // Accedemos a la base de datos en tiempo real
            const imagesRef = ref(db, `images/${userId}`); // Referencia a las imágenes del usuario

            try {
                const snapshot = await get(imagesRef); // Obtenemos los datos de la referencia

                if (snapshot.exists()) {
                    const imagesData = snapshot.val(); // Obtenemos los datos de las imágenes

                    // Transformar los datos a un formato adecuado para el componente
                    const imagesArray = Object.keys(imagesData).map((key) => ({
                        id: key,
                        name: imagesData[key].imageName,
                        url: imagesData[key].dataURL,
                        storyText: imagesData[key].storyText
                    }));

                    setImages(imagesArray);
                } else {
                    console.log('No images found.');
                    setImages([]);
                }
            } catch (error) {
                console.error('Error fetching images from Firebase Realtime Database:', error);
            }
        };

        fetchImages();
    }, [userId]);

    const handleEdit = (image) => {
        setEditingImage(image);
        setNewName(image.name);
        setNewStoryText(image.storyText);
    };

    const handleSave = async () => {
        if (!editingImage) return;

        const db = getDatabase();
        const imageRef = ref(db, `images/${userId}/${editingImage.id}`);

        try {
            await update(imageRef, {
                imageName: newName,
                storyText: newStoryText,
            });
            // Actualizar el estado de las imágenes para reflejar los cambios
            setImages((prevImages) =>
                prevImages.map((image) =>
                    image.id === editingImage.id
                        ? { ...image, name: newName, storyText: newStoryText }
                        : image
                )
            );
            setEditingImage(null); // Cerrar el modal de edición
            Swal.fire({
                icon: 'success',
                title: t('success'),
                text: t('textDetails'),
                confirmButtonText: 'OK'
            });
        } catch (error) {
            console.error('Error updating image:', error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: 'There was an issue updating the image title and story.',
                confirmButtonText: 'Try Again'
            });
        }
    };

    const handleCancel = () => {
        setEditingImage(null); // Cerrar el modal de edición sin guardar cambios
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: t('confirmDelete'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('delete'),
            cancelButtonText: t('cancel'),
            customClass: {
                confirmButton: Style.swal2ConfirmBtnDelete,
                cancelButton: Style.swal2CancelBtnCancel,
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                const db = getDatabase();
                const imageRef = ref(db, `images/${userId}/${id}`);
                try {
                    // Eliminar la imagen de la base de datos
                    await remove(imageRef);
                    setImages((prevImages) => prevImages.filter((image) => image.id !== id));
                    Swal.fire({
                        icon: 'success',
                        title: t('deleted'),
                        text: t('imageDeleted'),
                        confirmButtonText: 'OK',
                    });
                } catch (error) {
                    console.error('Error deleting image:', error);
                    Swal.fire({
                        icon: 'error',
                        title: t('error'),
                        text: 'There was an issue deleting the image.',
                        confirmButtonText: 'Try Again',
                    });
                }
            }
        });
    };

    const handleShare = async (imageId) => {
        const imageToShare = images.find((image) => image.id === imageId);
        if (!imageToShare) return;
    
        const { url, name, storyText } = imageToShare;
    
        // Obtener el usuario autenticado
        const auth = getAuth();
        const user = auth.currentUser;  // Obtén el usuario actual
    
        // Si el usuario está autenticado, obtenemos su nombre (o 'Desconocido' si no tiene nombre)
        const userName = user ? user.displayName || 'Desconocido' : 'Desconocido';
    
        const db = getDatabase();
        const sharedRef = ref(db, `shared/images/`);
    
        try {
            // Función para verificar si existe una imagen con el mismo nombre y la misma historia en shared/images
            const checkIfExists = async (imageName, imageStory) => {
                const snapshot = await get(sharedRef);
                if (snapshot.exists()) {
                    const imagesData = snapshot.val();
                    return Object.values(imagesData).some(image => image.imageName === imageName && image.storyText === imageStory);
                }
                return false;
            };
    
            const imageExists = await checkIfExists(name, storyText);
    
            if (imageExists) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Esta imagen ya ha sido compartida',
                    text: 'La imagen con el mismo nombre y la misma historia ya está disponible.',
                    confirmButtonText: 'OK',
                });
                return;
            }
    
            // Guardar la imagen con el nuevo imageId y el nombre del usuario que la compartió
            const imageRef = ref(db, `shared/images/${imageId}`);
            await update(imageRef, {
                imageName: name,
                storyText: storyText,
                imageURL: url,
                userName: userName, // Guardar el nombre del usuario
            });
    
            Swal.fire({
                icon: 'success',
                title: 'Imagen compartida',
                text: 'La imagen ha sido compartida exitosamente.',
                confirmButtonText: 'OK',
            });
        } catch (error) {
            console.error('Error sharing image:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Hubo un problema al compartir la imagen.',
                confirmButtonText: 'Intentar de nuevo',
            });
        }
    };

    return (
        <>
            <div className={Style.myDrawingContainer}>
                <h1 className={Style.titleContainer}>{t('myDrawings')}</h1>
                <div className={Style.articleWrapperAdd} style={{ marginTop: '-2.5rem' }}>
                    <article className={Style.roundedLgAdd} onClick={() => navigate('/draw2D')}>
                        <div className={Style.plusIconContainer}>
                            <FontAwesomeIcon icon={faPlus} size="3x" />
                        </div>
                        <div className={Style.titleImageContainerAdd}>
                            <h3 className={Style.imageTitle}>{t('myDrawingsAdd')}</h3>
                        </div>
                    </article>
                </div>
                <div className={Style.articleWrapper}>
                    {images.length > 0 && images.map(({ url, name, storyText, id }, index) => (
                        <article key={index} className={Style.roundedLg}>
                            <img src={url} alt={`Drawing ${index}`} className={Style.image} />
                            <div className={Style.titleImageContainer}>
                                <h3 className={Style.imageTitle}>
                                    <button onClick={() => handleDelete(id)} className={Style.deleteButton}>
                                        {t('delete')}
                                    </button>
                                    {name.toUpperCase()}
                                    <button onClick={() => handleEdit({ name, storyText, id })} className={Style.editButton}>
                                        {t('edit')}
                                    </button>
                                    <button onClick={() => handleShare(id)} className={Style.shareButton}>
                                        {t('share')}
                                    </button>
                                </h3>
                                <p className={Style.imageStory}>{storyText}</p>
                            </div>
                        </article>
                    ))}
                </div>
                {editingImage && (
                    <div className={Style.modalOverlay}>
                        <div className={Style.modalContainer}>
                            <h2>{t('myDrawingsEdit')}</h2>
                            <div className={Style.inputGroup}>
                                <label htmlFor="newName">{t('name')}</label>
                                <input
                                    id="newName"
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    placeholder={t('placeholderName')}
                                />
                            </div>
                            <div className={Style.inputGroup}>
                                <label htmlFor="newStoryText">{t('story')}</label>
                                <textarea
                                    id="newStoryText"
                                    value={newStoryText}
                                    onChange={(e) => setNewStoryText(e.target.value)}
                                    placeholder={t('placeholderStory')}
                                />
                            </div>
                            <div className={Style.modalActions}>
                                <button className={Style.cancelEdit} onClick={handleCancel}>{t('cancel')}</button>
                                <button className={Style.saveEdit} onClick={handleSave}>{t('save')}</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default MyDrawings;
